import './VorPhase.css';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import vorData from './vorData.json';

const VELOCITY_THRESHOLD = 0.15;
const DISTANCE_THRESHOLD = 20;

// ── 4-point scale config ──
// DOM order drives 2×2 grid layout: top-left=strong neg, top-right=strong pos, bottom rows=moderate
const VOTE_OPTIONS = [
  { value: 'strongly_disagree', tKey: 'vor.vote.strongly_disagree', side: 'neg' },
  { value: 'strongly_agree',    tKey: 'vor.vote.strongly_agree',    side: 'pos' },
  { value: 'disagree',          tKey: 'vor.vote.disagree',          side: 'neg' },
  { value: 'agree',             tKey: 'vor.vote.agree',             side: 'pos' },
];
const AGREE_SIDE = ['agree', 'strongly_agree'];
const VOTE_BADGE = {
  strongly_disagree: 'vor.voted.strongly_disagreed',
  disagree:          'vor.voted.disagreed',
  agree:             'vor.voted.agreed',
  strongly_agree:    'vor.voted.strongly_agreed',
};
const VOTE_ICON = {
  strongly_disagree: '↓↓',
  disagree:          '↓',
  agree:             '↑',
  strongly_agree:    '↑↑',
};

export default function VorPhase() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const lang = i18n.language;

  // ── State ──
  // Start directly at the positioning step - no topic selection.
  const [step, setStep]           = useState('position'); // 'position' | 'confirm'
  const [votes, setVotes]         = useState({});          // thesisId → vote value
  const [currentIdx, setCurrentIdx] = useState(0);

  // ── Refs for gesture control ──
  const trackRef = useRef(null);
  const idxRef   = useRef(0);

  const topics = vorData.topics;
  const theses = vorData.theses; // all 8 theses - no topic filter

  // ── i18n getters ──
  const getTitle = (obj) => {
    if (lang === 'de') return obj.title_de;
    if (lang === 'fr') return obj.title_fr;
    return obj.title_en;
  };

  const getText = (obj) => {
    if (lang === 'de') return obj.text_de;
    if (lang === 'fr') return obj.text_fr;
    return obj.text_en;
  };

  // ── Step transition ──
  const goToStep = (nextStep) => {
    if (nextStep === 'confirm' && trackRef.current) {
      trackRef.current.style.transition = 'none';
      trackRef.current.style.transform  = 'none';
    }
    setStep(nextStep);
    if (nextStep === 'position') {
      setCurrentIdx(0);
      idxRef.current = 0;
    }
  };

  // ── Carousel: low-level move ──
  const moveTo = (idx, animated = true, snapBack = false) => {
    const track = trackRef.current;
    if (!track) return;
    if (animated) {
      track.getBoundingClientRect(); // flush layout so dragged position is committed before transition
      track.style.transition = snapBack
        ? 'transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)'
        : 'transform 0.9s cubic-bezier(0.16, 1, 0.3, 1)';
    } else {
      track.style.transition = 'none';
    }
    track.style.transform = `translateY(calc(-${idx} * 100dvh))`;
  };

  const navigateTo = (idx) => {
    const clamped = Math.max(0, Math.min(theses.length - 1, idx));
    const track = trackRef.current;
    if (track) {
      const live = getComputedStyle(track).transform;
      track.style.transition = 'none';
      track.style.transform = live;
    }
    setCurrentIdx(clamped);
    idxRef.current = clamped;
    moveTo(clamped, true);
  };

  // ── Position step: gestures + initialization ──
  useEffect(() => {
    if (step !== 'position') return;

    // Initialize track at slide 0
    moveTo(0, false);

    // Measure navbar + dock heights for CSS variables
    const measure = () => {
      const navbar = document.querySelector('.navbar');
      const dock   = document.querySelector('.vor-vote-dock');
      if (navbar) {
        document.documentElement.style.setProperty(
          '--vor-nav-h', navbar.getBoundingClientRect().height + 'px'
        );
      }
      if (dock) {
        document.documentElement.style.setProperty(
          '--vor-dock-h', dock.getBoundingClientRect().height + 'px'
        );
      }
    };
    const timer = setTimeout(measure, 60);
    window.addEventListener('resize', measure);

    // Touch + wheel gestures
    const touch = { startY: 0, lastY: 0, lastT: 0, velocity: 0 };

    const onTouchStart = (e) => {
      touch.startY   = e.touches[0].clientY;
      touch.lastY    = touch.startY;
      touch.lastT    = Date.now();
      touch.velocity = 0;
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      const now = Date.now();
      const y   = e.touches[0].clientY;
      const dt  = now - touch.lastT;
      if (dt > 0) touch.velocity = (touch.lastY - y) / dt;
      touch.lastY = y;
      touch.lastT = now;
      const dragOffset = touch.startY - y;
      const track = trackRef.current;
      if (track) {
        track.style.transition = 'none';
        track.style.transform = `translateY(calc(-${idxRef.current} * 100dvh - ${dragOffset}px))`;
      }
    };

    const onTouchEnd = () => {
      const distance = touch.startY - touch.lastY;
      const triggered =
        Math.abs(touch.velocity) > VELOCITY_THRESHOLD ||
        Math.abs(distance)       > DISTANCE_THRESHOLD;
      if (triggered) {
        navigateTo(idxRef.current + (distance > 0 ? 1 : -1));
      } else {
        moveTo(idxRef.current, true, true); // snapBack
      }
    };

    let wheelTimer = null;
    let wheelDelta = 0;
    const onWheel = (e) => {
      e.preventDefault();
      wheelDelta += e.deltaY;
      const track = trackRef.current;
      if (track) {
        track.style.transition = 'none';
        track.style.transform = `translateY(calc(-${idxRef.current} * 100dvh - ${wheelDelta}px))`;
      }
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        const triggered = Math.abs(wheelDelta) > DISTANCE_THRESHOLD;
        if (triggered) {
          navigateTo(idxRef.current + (wheelDelta > 0 ? 1 : -1));
        } else {
          moveTo(idxRef.current, true, true); // snapBack
        }
        wheelDelta = 0;
      }, 80);
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true  });
    document.addEventListener('touchmove',  onTouchMove,  { passive: false });
    document.addEventListener('touchend',   onTouchEnd,   { passive: true  });
    document.addEventListener('wheel',      onWheel,      { passive: false });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', measure);
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove',  onTouchMove);
      document.removeEventListener('touchend',   onTouchEnd);
      document.removeEventListener('wheel',      onWheel);
      clearTimeout(wheelTimer);
    };
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Voting ──
  const currentThesis   = theses[currentIdx];
  const currentThesisId = currentThesis?.id;
  const currentVote     = votes[currentThesisId];

  const handleVote = (v) => {
    setVotes(prev => ({ ...prev, [currentThesisId]: v }));
  };

  const handleNextOrFinish = () => {
    if (currentIdx < theses.length - 1) {
      navigateTo(currentIdx + 1);
    } else {
      goToStep('confirm');
    }
  };

  // ── POSITIONING STEP ──
  if (step === 'position') return (
    <div className="vor-root">

      {/* Slide track - one slide per thesis */}
      <div className="vor-slides-track" ref={trackRef}>
        {theses.map((thesis, i) => {
          const topic = topics.find(tp => tp.id === thesis.topicId);
          return (
            <div key={thesis.id} className="vor-slide">
              <div className="vor-slide-inner">
                <div className="vor-slide-header">
                  <span className="vor-phase-badge">{t('vor.position.badge')}</span>
                  <span className="vor-slide-progress">{i + 1} / {theses.length}</span>
                </div>
                <div className="vor-slide-topic">{topic ? getTitle(topic) : ''}</div>
                <p className="vor-slide-thesis">
                  <span className="vor-q">&ldquo;</span>
                  {getText(thesis)}
                  <span className="vor-q">&rdquo;</span>
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress dots - one per thesis */}
      <div className="takes-dots">
        {theses.map((_, i) => (
          <div
            key={i}
            className={`takes-dot${i === currentIdx ? ' active' : ''}`}
            onClick={() => navigateTo(i)}
          />
        ))}
      </div>

      {/* Vote dock */}
      <div className="vor-vote-dock">
        <div className="vor-vote-dock-inner">

          <div className="vor-dock-row">
            <span className="vor-dock-label">{currentIdx + 1} / {theses.length}</span>
            {currentVote && (
              <span className={`vor-dock-voted-badge${AGREE_SIDE.includes(currentVote) ? ' agree' : ' disagree'}`}>
                {t(VOTE_BADGE[currentVote])}
              </span>
            )}
          </div>

          {/* 4-point scale - 2×2 grid: left col = disagree, right col = agree */}
          <div className="vor-vote-scale">
            {VOTE_OPTIONS.map(({ value, tKey, side }) => (
              <button
                key={value}
                className={[
                  'vor-vote-opt',
                  `vor-vote-opt--${side}`,
                  currentVote === value ? 'active' : '',
                  currentVote && currentVote !== value ? 'dim' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => handleVote(value)}
              >
                {t(tKey)}
              </button>
            ))}
          </div>

          <button
            className={`vor-next-btn${currentVote ? ' active' : ''}`}
            disabled={!currentVote}
            onClick={handleNextOrFinish}
          >
            {currentIdx < theses.length - 1 ? t('vor.next.thesis') : t('vor.complete')}
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 3l5 5-5 5" />
            </svg>
          </button>

        </div>
        <div className="takes-proto">Prototype by Ferdinand Riedl · riedlsoftware.com</div>
      </div>
    </div>
  );

  // ── CONFIRMATION ──
  return (
    <div className="vor-root">
      <div className="vor-confirm-wrap">
        <div className="vor-confirm-content">

          <div className="vor-confirm-icon">
            <svg viewBox="0 0 52 52" fill="none">
              <circle cx="26" cy="26" r="24" stroke="currentColor" strokeWidth="1.5" opacity="0.18" />
              <circle cx="26" cy="26" r="24" stroke="currentColor" strokeWidth="1.5" strokeDasharray="150" strokeDashoffset="0" opacity="0.6" />
              <path d="M16 26l8 8 12-12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <h2 className="vor-confirm-title">{t('vor.confirm.title')}</h2>
          <p className="vor-confirm-sub">
            <strong>{t('vor.confirm.startDate')}</strong>
          </p>

          <button
            className="vor-cta-btn active vor-confirm-cta"
            onClick={() => history.push('/takes')}
          >
            {t('vor.confirm.cta')}
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 3l5 5-5 5" />
            </svg>
          </button>

          <div className="takes-proto" style={{ marginTop: 20, textAlign: 'center' }}>
            Prototype by Ferdinand Riedl · riedlsoftware.com
          </div>

        </div>
      </div>
    </div>
  );
}
